/**
 * «Переименовать вариант плана» — карандаш у активного чипа переключателя.
 *
 * Требование founder (прод-UAT 21.08, экран «Посевной план · сезон 2027»):
 * «вариант посевного плана — можно менять его имя». Вариант заводился как
 * «Вариант 1», и это имя оставалось навсегда — а обсуждают план именно по
 * имени («смотри Интенсив»).
 *
 * Почему карандаш у чипа, а не пункт меню. Так уже решено на соседних экранах
 * этого же раздела — этапы техкарты (`tech-cards/StagesPanel`) правятся
 * ghost-кнопкой с `Pencil` рядом со строкой. Второй паттерн на том же разделе
 * пришлось бы объяснять.
 *
 * Почему диалог, а не inline-редактирование чипа. Чип живёт в ряду других
 * чипов: превращаясь в поле ввода, он раздвигает ряд и остальные варианты
 * прыгают под курсором. Диалог — та же форма, что и у «+ Новый вариант»
 * (`CreateVariantButton`), человек видит знакомое.
 *
 * Право — `season_plan:U`, ровно то же, которым вариант заводится: кто может
 * завести «Вариант 1», тот может назвать его «Интенсив». Без права карандаша
 * нет вовсе (fail-closed `useCan` — в том числе пока грузится `/me`).
 */
import { useEffect, useState } from 'react';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { describeApiError } from '@/lib/apiError';
import { useCan } from '@/lib/auth/usePermissions';
import { useRenamePlanVariant } from '@/lib/planning/techCards';
import { VARIANT_NAME_MAX, variantNameError } from '@/lib/planning/variantName';

export function RenameVariantButton({
  variantId,
  currentName,
  /** Названия ВСЕХ вариантов хозяйства на сезон — включая переименовываемый. */
  existingNames,
}: {
  variantId: string;
  currentName: string;
  existingNames: readonly string[];
}) {
  const canRename = useCan('season_plan', 'u');
  const rename = useRenamePlanVariant();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(currentName);
  /** Отказ сервера (409 «имя занято» при гонке, 403, сеть) — отдельно от локального. */
  const [serverError, setServerError] = useState<string | null>(null);

  // Диалог открывается с ТЕКУЩИМ именем: переименование — это правка, а не
  // ввод с нуля, и заставлять перепечатывать «Интенсив» ради одной буквы незачем.
  useEffect(() => {
    if (open) {
      setName(currentName);
      setServerError(null);
    }
  }, [open, currentName]);

  if (!canRename) return null;

  const localError = variantNameError(name, existingNames, currentName);
  const unchanged = name.trim() === currentName.trim();
  const blocked = !!localError || rename.isPending;

  const submit = () => {
    if (blocked) return;
    // Ничего не поменяли — просто закрываем: слать PATCH ради того же имени
    // значит показать «переименован» там, где ничего не произошло.
    if (unchanged) {
      setOpen(false);
      return;
    }
    setServerError(null);
    rename.mutate(
      { scenarioId: variantId, name: name.trim() },
      {
        onSuccess: () => setOpen(false),
        // Диалог остаётся открытым с введённым текстом: закрыть его на отказе
        // значит съесть работу человека и показать успех там, где его нет.
        onError: (err) => setServerError(describeApiError(err)),
      },
    );
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="h-8 w-8 p-0"
        aria-label={`Переименовать вариант ${currentName}`}
        title="Переименовать вариант"
        onClick={() => setOpen(true)}
      >
        <Pencil className="h-3.5 w-3.5" />
      </Button>
      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Переименовать вариант плана</DialogTitle>
            <DialogDescription>
              Новое имя увидят все, кто работает с этим сезоном: переключатель вариантов,
              структура посевных площадей, назначение техкарт и сводка. Сам план —
              культуры, техкарты, цифры — не меняется.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="rename-variant-name">Название варианта</Label>
              <Input
                id="rename-variant-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setServerError(null);
                }}
                autoFocus
                maxLength={VARIANT_NAME_MAX}
                aria-invalid={!!localError || !!serverError}
              />
              {localError && <p className="text-sm text-destructive">{localError}</p>}
              {!localError && serverError && (
                <p className="text-sm text-destructive">{serverError}</p>
              )}
            </div>
            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Отмена
              </Button>
              <Button type="submit" disabled={blocked}>
                {rename.isPending ? 'Сохраняем…' : 'Сохранить'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
